<script>
    import _ from 'lodash';
    import moment from 'moment';
    import {Link} from 'svelte-routing';
    import {network, store} from '../stores.js';
    import TopMenu from '../components/TopMenu.svelte';
    import BottomMenu from '../components/BottomMenu.svelte';
    import TaskCalendar from '../components/TaskCalendar.svelte';
    import {
        STATUSES,
        getStatus,
        getStatusKey,
        isValidStatusFilter,
        isRejected,
        isCorrected,
        rejectionNotes,
    } from '../lib/orderStatus.js';


    const user = store('credential', 'getAuth');
    const axis = store('axis', '', false);

    // planDvizhDeneg / oplatDvizhDenegApp expose nomDoc; viborki uses nomer
    const orderId = (item) => item?.nomer ?? item?.nomDoc;

    const listItemKey = (item, index) =>
        `${orderId(item) ?? ''}-${item?.vid ?? ''}-${item?.tip ?? ''}-${index}`;

    const kommentParts = (komment) =>
        String(komment || '').split('|').map((s) => s.trim()).filter(Boolean);

    const toArray = (data) => {
        if (!data) return [];
        return Array.isArray(data) ? data : [data];
    };

    /** Same window Order.svelte uses when loading finance rows for a single order. */
    function listDateRange() {
        return {
            from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
            to: moment().add(1, 'days').format('YYYY-MM-DD'),
        };
    }

    function monthRangeStr(date) {
        const from = moment(date).startOf('month').format('YYYY-MM-DD');
        const to = moment(date).endOf('month').add(1, 'day').format('YYYY-MM-DD');
        return { from, to };
    }

    /**
     * Shared list+calendar source:
     * planDvizhDeneg (plans / tip=1) + oplatDvizhDenegApp (payments / tip=0 paid)
     * for the given pay-list date window. Raw rows are not mutated.
     */
    async function buildDisplayList(from, to) {
        const [planRaw, payRaw] = await Promise.all([
            network.getPlanListRange(+$user.id),
            network.getPayListRange(+$user.id, from, to),
        ]);

      /*  console.log('RAW planList[0..2]', JSON.stringify(planRaw.slice(0, 2), null, 2));
        console.log('RAW orderList[0..2]', JSON.stringify(payRaw.slice(0, 2), null, 2));
*/
        const list = [...toArray(planRaw), ...toArray(payRaw)];
        const ids = list.map(orderId).filter((id) => id != null && id !== '');
        const chat = ids.length
            ? await network.getChatMessagesCount(ids, +$user.id)
            : [];

        return list.map((item) => {
            const id = String(orderId(item) ?? '');
            const buf = chat.find((el) => el.order != null && String(el.order) === id);
            return { ...item, chat: buf ? buf.count : 0 };
        });
    }

    let search = '';
    let orders = buildDisplayList(...Object.values(listDateRange()));

    let viewMode = 'list'; // 'list' | 'calendar'
    let calendarMonth = new Date();

    function setViewMode(mode) {
        viewMode = mode;
    }

    let calendarOrders = buildDisplayList(...Object.values(monthRangeStr(calendarMonth)));

    function onCalendarMonthChange(event) {
        calendarMonth = event.detail.month;
        const { from, to } = monthRangeStr(calendarMonth);
        calendarOrders = buildDisplayList(from, to);
    }

    const filter = _.debounce(async () => {
        let data = await buildDisplayList(...Object.values(listDateRange()));

        if (search.trim()) {
            const q = search.trim();
            return orders = data.filter((item) => {
                return (
                    (item.name || '').indexOf(q) !== -1 ||
                    (item.komment || '').indexOf(q) !== -1 ||
                    (item.kommentKontr || '').indexOf(q) !== -1 ||
                    (item.kommentKazna || '').indexOf(q) !== -1
                )
            });
        }

        orders = data;
    }, 1000);


    const STATUS_FILTER_STORAGE_KEY = 'dashboard_status_filter';

    function loadSavedStatuses() {
        try {
            const raw = localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
            // null / invalid → «Все» (no filter); DEFAULT_STATUSES is only for key validation
            if (raw === null) return [];
            const parsed = JSON.parse(raw);
            // migrate away from old vid-based keys ('2','3','4') and legacy "all three selected"
            if (!isValidStatusFilter(parsed)) return [];
            // single-select UX: collapse legacy multi-select saves to «Все»
            if (parsed.length > 1) return [];
            return parsed;
        } catch (e) {
            return [];
        }
    }

    function saveStatuses(statuses) {
        try {
            localStorage.setItem(STATUS_FILTER_STORAGE_KEY, JSON.stringify(statuses));
        } catch (e) {
            // localStorage может быть недоступен (приватный режим) — просто игнорируем
        }
    }

    let selectedStatuses = loadSavedStatuses();

    function toggleStatus(key) {
        if (key === 'all') {
            selectedStatuses = [];
        } else if (selectedStatuses.length === 1 && selectedStatuses[0] === key) {
            selectedStatuses = []; // clicking active filter again resets to «Все»
        } else {
            selectedStatuses = [key]; // single-select: only this status
        }
        saveStatuses(selectedStatuses);
    }

    /** Pure filter: pass statuses explicitly so reactive `$:` lines track selectedStatuses. */
    function applyStatusFilter(list, statuses) {
        if (!statuses.length) return list;
        return list.filter((item) => {
            const key = getStatusKey(item);
            return key != null && statuses.includes(key);
        });
    }


    let resolvedList = [];
    let ordersLoading = true;

    $: orders.then(data => {
        resolvedList = data;
        ordersLoading = false;
    });

    // selectedStatuses must appear on this line (not only inside a helper) for instant UI updates
    $: filteredList = applyStatusFilter(resolvedList, selectedStatuses);



    let resolvedCalendarList = [];
    let calendarLoading = true;

    $: calendarOrders.then(data => {
        resolvedCalendarList = data;
        calendarLoading = false;
    });

    $: filteredCalendarList = applyStatusFilter(resolvedCalendarList, selectedStatuses);

</script>

<TopMenu title="Мои заявки" />

<div class="extraHeader dashboard-header  ">
    <form  >
        <div class="form-group searchbox">
            <input type="text" class="form-control" placeholder="Поиск..." bind:value={search} on:keyup={filter}>
            <i class="input-icon">
                <ion-icon name="search-outline" role="img" class="md hydrated" aria-label="search outline"></ion-icon>
            </i>
        </div>
    </form>

    <div class="status-filter">
        <button
                class="status-chip {selectedStatuses.length === 0 ? 'active' : ''}"
                on:click={() => toggleStatus('all')}
        >
            Все
        </button>
        {#each STATUSES as s (s.key)}
            <button
                    class="status-chip {selectedStatuses.includes(s.key) ? 'active' : ''}"
                    style="--chip-color: {s.color}; {selectedStatuses.includes(s.key) ? `color: ${s.colort};` : ''}"
                    on:click={() => toggleStatus(s.key)}
            >
                {s.label}
            </button>
        {/each}
    </div>

    <div class="view-toggle">
        <button class="toggle-btn {viewMode === 'list' ? 'active' : ''} px-3" on:click={() => setViewMode('list')}>
            Список
        </button>
        <button class="toggle-btn {viewMode === 'calendar' ? 'active' : ''} px-3" on:click={() => setViewMode('calendar')}>
            Календарь
        </button>
    </div>
</div>

<div id="appCapsule" class="extra-header-active">
    {#if ordersLoading}
        <div class="section mt-2">
            <div class="card text-center">
                <div class="card-body p-5">
                    <h5 class="card-title">Загрузка</h5>
                    <p class="card-text">
                        Пожалуйста подождите, мы загружаем список ваших заявок.
                    </p>
                </div>
            </div>
        </div>
    {:else if viewMode === 'list'}
        <div class="section full">
            <div class="bg-white">
                <ul class="listview link-listview">
                    {#each filteredList as item, index (listItemKey(item, index))}
                        <li
                            class:order-row--rejected={isRejected(item) && !isCorrected(item)}
                            class:order-row--corrected={isCorrected(item)}
                        >
                            <Link to="/order/{orderId(item)}/{item.vid}" class="row py-3">
                                        <div class="col-12">
                                            <div class="row">
                                                <div class="col-12 col-md-6">
                                                    <div class="row">
                                                        <div class="col-12">
                                                            <h4 class="order-title d-inline">
                                                                {#if item.nomDoc}<span class="order-num">{item.nomDoc}</span>{/if}
                                                                {#if item.proekt}<span class="order-project">{item.proekt}</span>{/if}
                                                            </h4>
                                                            <span class="text-muted">{moment(item.data).format('DD.MM.YYYY')}</span>
                                                        </div>
                                                        <div class="col-12">
                                                            {#if item.kontrName}
                                                                <div class="order-kontr">{item.kontrName}</div>
                                                            {/if}
                                                            {#if kommentParts(item.komment).length}
                                                                <div class="order-komment">
                                                                    {#each kommentParts(item.komment) as part, i (i)}
                                                                        <div
                                                                            class="order-komment-line"
                                                                            class:order-komment-meta={i > 0}
                                                                        >{part}</div>
                                                                    {/each}
                                                                </div>
                                                            {/if}
                                                            {#if isRejected(item)}
                                                                <div class="rejection-notes">
                                                                    {#each rejectionNotes(item) as note (note.label)}
                                                                        <div class="rejection-note" class:rejection-note--fixed={note.corrected}>
                                                                            <span class="rejection-note-label">{note.label}:</span>
                                                                            {note.text}
                                                                        </div>
                                                                    {/each}
                                                                </div>
                                                            {/if}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-12 col-md-6">
                                                    <div class="row text-end">
                                                        <div class="col-12 mb-2">
                                                            <!--<div class="chip chip-media">
                                                                <Link to="/chat/{orderId(item)}">
                                                                    <i class="chip-icon bg-dark">
                                                                        <ion-icon name="chatbox-ellipses-outline" role="img" class="md hydrated"></ion-icon>
                                                                    </i>
                                                                    <span class="chip-label text-black">{@html item.chat ? item.chat : "&nbsp;"}</span>
                                                                </Link>
                                                            </div>-->
                                                            {#each [getStatus(item)].filter(Boolean) as status (status.key)}
                                                                {#if status}
                                                                    <div class="chip {status.chipClass}" style="background: {status.color}!important; color: {status.colort}!important;">
                                                                        <span class="chip-label">{status.label}</span>
                                                                    </div>
                                                                {/if}
                                                            {/each}
                                                        </div>
                                                        <div class="col-12">
                                                            {#if +item.summa}
                                                                <div class="chip chip-media">
                                                                    <i class="chip-icon bg-primary"><ion-icon name="cash-outline"></ion-icon></i>
                                                                    <span class="chip-label">{(+item.summa).toFixed(0)} ₽</span>
                                                                </div>
                                                            {/if}
                                                            {#if +item.summaZakrit}
                                                                <div class="chip chip-media">
                                                                    <i class="chip-icon bg-success"><ion-icon name="cash-outline"></ion-icon></i>
                                                                    <span class="chip-label">{(+item.summaZakrit).toFixed(0)} ₽</span>
                                                                </div>
                                                            {/if}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                            </Link>
                        </li>
                    {/each}
                </ul>
                {#if filteredList.length === 0}
                    <div class="empty-filter-state">
                        <p>Нет заявок с выбранным статусом</p>
                    </div>
                {/if}
            </div>
        </div>
    {:else}
        {#if calendarLoading}
            <div class="section mt-2">
                <div class="card text-center">
                    <div class="card-body p-5">
                        <p class="card-text">Загрузка календаря...</p>
                    </div>
                </div>
            </div>
        {:else}
            <TaskCalendar tasks={filteredCalendarList} bind:currentMonth={calendarMonth} on:monthChange={onCalendarMonthChange} />
        {/if}
    {/if}
</div>

<BottomMenu />


<style>
    .view-toggle {
        display: flex;
        gap: 4px;
        padding: 4px;

        background: #f1f3f5;
        border-radius: 14px;
    }

    .toggle-btn {
        flex: 1;
        padding: 9px 0;
        border: none;
        border-radius: 10px;
        background: transparent;
        font-size: 13px;
        font-weight: 600;
        color: #6b7280;
        cursor: pointer;
        transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
        -webkit-tap-highlight-color: transparent;
    }

    .toggle-btn:active {
        transform: scale(0.97);
    }

    .toggle-btn.active {
        background: #ffffff;
        color: #0b7285;
        box-shadow: 0 2px 6px rgba(11, 114, 133, 0.14);
    }

    .status-filter {
        display: flex;
        flex-wrap: nowrap;
        gap: 6px;
        padding: 10px 10px 10px 10px;
        overflow-x: auto;
        overflow-y: hidden;
        white-space: nowrap;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .status-filter::-webkit-scrollbar {
        display: none;
    }

    .status-chip {
        flex: 0 0 auto;
        padding: 6px 12px;
        border-radius: 999px;
        border: 1.5px solid #d8dde3;
        background: #fff;
        font-size: 12px;
        font-weight: 600;
        color: #6b7280;
        cursor: pointer;
        white-space: nowrap;
        transition: all 0.15s ease;
        -webkit-tap-highlight-color: transparent;
    }

    .status-chip:active {
        transform: scale(0.96);
    }

    .status-chip.active {
        background: var(--chip-color, #0b7285);
        border-color: var(--chip-color, #0b7285);
        color: #fff;
    }

    .empty-filter-state {
        text-align: center;
        padding: 40px 20px;
        color: #9ca3af;
        font-size: 14px;
    }

    .chip-new {
        background: #42a5f5!important;
    }
    .chip-warning {
        background: #66bb6a!important;
    }
    .chip-success {
        background: #d4e157!important;
        color: #0c1624!important;
    }

    .order-row--rejected,
    .order-row--rejected :global(a) {
        background: #fef2f2;
    }

    .order-row--rejected :global(a:active),
    .order-row--rejected :global(a:hover) {
        background: #fee2e2;
    }

    .order-row--corrected,
    .order-row--corrected :global(a) {
        background: #ecfdf3;
    }

    .order-row--corrected :global(a:active),
    .order-row--corrected :global(a:hover) {
        background: #d1fae5;
    }

    .rejection-notes {
        margin-top: 8px;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .rejection-note {
        color: #b42318;
        font-size: 12px;
        line-height: 1.35;
        white-space: normal;
    }

    .rejection-note--fixed {
        color: #067647;
    }

    .rejection-note-label {
        font-weight: 600;
    }

    .order-num {
        font-weight: 500;
        font-size: 0.85em;
        color: #9ca3af;
        margin-right: 6px;
    }

    .order-project {
        font-weight: 700;
    }

    .order-kontr {
        margin: 8px 0;
    }

    .order-komment {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .order-komment-line {
        font-size: 14px;
        color: #111;
        line-height: 1.4;
    }

    .order-komment-meta {
        font-size: 13px;
        color: #6b7280;
    }

    .dashboard-header {
        display: flex!important;
        justify-content: space-between;
        flex-direction: row;

    }
    .extraHeader{
        height: auto;
    }

    @media (max-width: 880px) {
        .dashboard-header {
            display: flex!important;
            flex-direction: column!important;

        }

        #appCapsule {
            padding: 215px 0!important;
        }
    }




</style>